import React, { useCallback, useState } from 'react';
import TrueImpactFileUploadBox, {
  TrueImpactFile,
} from 'shared/design-system/components/TrueImpactFileUploadBox';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { fileUpload } from '../IndicatorDetails/utils';
import StoryFileRender from './StoryFileRender';

export interface StoryFile {
  file?: File;
  storyFileId?: number | string;
  storyFileName?: string;
  storyFileUrl?: string;
}

interface StoryFileUploadProps {
  reportId: string | number;
  storyFileId?: string;
  storyFileUrl?: string;
  storyFileName?: string;
  handleFileUpload: (file: StoryFile | undefined) => unknown;
}

const MAX_FILE_SIZE = 512 * 1000 * 1000; // 512mb
const acceptedFileString = 'JPG, JPEG, PNG';
const allowedFileExtensions = ['image/png', 'image/jpeg', 'image/jpg'];

const StoryFileUpload: React.FC<StoryFileUploadProps> = ({
  reportId,
  storyFileId,
  storyFileName,
  storyFileUrl,
  handleFileUpload,
}) => {
  const [file, setFile] = useState<TrueImpactFile | undefined>(
    storyFileUrl
      ? {
          id: storyFileId ? parseInt(storyFileId) : undefined,
          fileName: storyFileName || 'Unknown',
          url: storyFileUrl,
        }
      : undefined,
  );

  const onFileUpload = useCallback(
    async (file: File | undefined) => {
      if (!file) {
        handleFileUpload(undefined);
        setFile(undefined);
        return;
      }
      if (!allowedFileExtensions.includes(file.type)) {
        setFile(s => ({
          ...s,
          file: undefined,
          fileName: undefined,
          error: `Sorry, ${file.type || 'that'} file format isn’t supported.`,
        }));
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setFile(s => ({
          ...s,
          file: undefined,
          fileName: undefined,
          error: `File is too big. Please try size under ${MAX_FILE_SIZE}MB`,
        }));
        return;
      }
      setFile(s => ({ ...s, isUploading: true }));
      const uploadResult = await fileUpload(reportId, file);
      if (uploadResult.success) {
        setFile(s => ({
          ...s,
          ...uploadResult,
          file,
          fileName: file.name,
          error: undefined,
          isUploading: false,
        }));
        handleFileUpload({
          file,
          storyFileId: uploadResult.id,
          storyFileName: file.name,
          storyFileUrl: uploadResult.url,
        });
      } else {
        setFile(s => ({
          ...s,
          file: undefined,
          fileName: undefined,
          isUploading: false,
          error:
            uploadResult.error?.response?.data?.errors?.detail ||
            uploadResult.error?.message ||
            'Something went wrong while uploading.',
        }));
      }
    },
    [setFile, handleFileUpload],
  );
  return (
    <TrueImpactFileUploadBox
      width="100%"
      height="auto"
      CustomSelectedComponent={StoryFileRender}
      defaultFileText="Drag your photo here or click to upload"
      padding={SPACERS.extraLarge}
      file={file}
      acceptedFileString={acceptedFileString}
      handleFileUpload={onFileUpload}
    />
  );
};

export default StoryFileUpload;
